import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { resetPassword } from "aws-amplify/auth";

import Form from "./Form";
import {
  clearEmailFromSessionStorage,
  getEmailFromSessionStorage,
} from "../../../lib/storage";
import Input from "./Input";
import useAuthErrors, { type ServerError } from "../hooks/useAuthErrors";
import Spinner from "../Spinner/Spinner";
import Button from "../Button/Button";
import LinkOut from "./LinkOut";
import SuccessMessage from "./SuccessMessage";
import Errors from "./Errors";

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState(getEmailFromSessionStorage());
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const {
    ACTIONS,
    ERRORS,
    errors,
    dispatch,
    getErrorsByInput,
    parseAndDispatchServerError,
  } = useAuthErrors();

  useEffect(() => {
    setMounted(true);
  }, []);

  const validateRequiredFields = () => {
    const emailAction = email ? ACTIONS.REMOVE_ERROR : ACTIONS.ADD_ERROR;
    dispatch({ type: emailAction, payload: ERRORS.EMAIL_REQUIRED_ERROR });

    if (!email) {
      return false;
    }

    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    recaptchaRef.current?.reset();

    if (loading) return;

    setLoading(true);
    dispatch({ type: ACTIONS.CLEAR_ERRORS });

    if (!validateRequiredFields()) {
      setLoading(false);
      return;
    }

    const recaptchaToken = await recaptchaRef.current?.executeAsync();
    if (!recaptchaToken) {
      dispatch({ type: ACTIONS.ADD_ERROR, payload: ERRORS.RECAPTCHA_ERROR });
      setLoading(false);
      return;
    }

    try {
      const { nextStep } = await resetPassword({
        username: email,
        options: {
          clientMetadata: {
            email,
            recaptchaToken,
            domain: window.location.host,
            returnPathTemplate: "/reset-password/",
          },
        },
      });
      setLoading(false);
      if (nextStep.codeDeliveryDetails) {
        setSent(true);
        clearEmailFromSessionStorage();
      }
    } catch (error) {
      let serverError = error as ServerError;
      setLoading(false);
      parseAndDispatchServerError(serverError);
    }
  };

  return (
    <Form onSubmit={handleSubmit} variant="bordered">
      {!!sent ? (
        <SuccessMessage title="Email sent">
          <p>We have sent a password reset email to {email}.</p>
          <p>Click the link in the email to reset your password.</p>
          <p>
            If you can&apos;t find the email, please check your spam or junk
            folder.
          </p>
        </SuccessMessage>
      ) : (
        <>
          {errors && errors.length > 0 && <Errors errors={errors} />}

          <Input
            id="forgotten-password-email-address"
            label="Email address"
            type="email"
            value={email}
            onChange={handleEmailChange}
            required
            errors={getErrorsByInput(errors, "email")}
          />

          <Button fullWidth type="submit">
            {loading ? <Spinner /> : "Send"}
          </Button>

          {mounted && (
            <ReCAPTCHA
              sitekey={import.meta.env.PUBLIC_RECAPTCHA_SITE_KEY}
              size="invisible"
              ref={recaptchaRef}
            />
          )}

          <LinkOut>
            <p>
              <span>I agree to Sparkol's </span>
              <a href="https://www.sparkol.com/website-terms" target="_blank">
                terms and conditions
              </a>
            </p>
            <p>
              <button
                className="hyperlink"
                onClick={() => window.history.back()}
                type="button"
              >
                Cancel
              </button>
            </p>
          </LinkOut>
        </>
      )}
    </Form>
  );
};

export default ForgotPasswordForm;
